"use client";

import {
  useEffect,
  useRef,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from "react";

/**
 * Scroll reveal (design system v2 §7), fades and lifts its children into place
 * the first time they enter the viewport, then disconnects.
 *
 * The hidden state lives behind a `reveal-ready` class set on <html> from this
 * effect, so a no-JS or failed-hydration render shows content normally instead
 * of a blank page. Motion collapses under prefers-reduced-motion via the global
 * rule in globals.css.
 *
 * Use for below-the-fold content. Above-the-fold entrances use the CSS-only
 * `.enter` animation instead, which needs no JS and can't flash.
 */
export function Reveal({
  children,
  delay = 0,
  className,
  as = "div",
}: {
  children: ReactNode;
  /** Stagger, in ms, applied as a transition-delay. */
  delay?: number;
  className?: string;
  /** Semantic wrapper, e.g. "li" so a revealed list item stays valid HTML. */
  as?: "div" | "section" | "li";
}) {
  // Widened to ElementType so one ref type serves every tag this renders.
  const Tag: ElementType = as;
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    document.documentElement.classList.add("reveal-ready");

    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.disconnect();
          }
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={(el: HTMLElement | null) => {
        ref.current = el;
      }}
      className={`reveal${className ? ` ${className}` : ""}`}
      style={{ "--reveal-delay": `${delay}ms` } as CSSProperties}
    >
      {children}
    </Tag>
  );
}
